import { apiRequest } from "../common/api";
import { getDeploymentConfig } from "../common/deployment-config";
import { fetchWithLogging } from "../common/fetch-logger";
import { session } from "../auth/session";
import type { Overrides } from "./keymap";

// Controller profiles: named key layouts every player sees and can play
// with, signed in or not (GET /controllers is public; api/src/controllers.rs).
// Adding one needs sign-in; changing or deleting one, being its maker or an
// admin (`editable`). Which one a game uses is the player's setting, in
// keymap.ts. The list is cached in the browser so a game opens with its
// profile's keys offline too, and read again at each load.

export interface ControllerProfile {
  id: string;
  name: string;
  keys: Overrides;
  updatedAt: string;
  /** The caller made it. */
  mine: boolean;
  /** The caller made it or is an admin, so may change or delete it. */
  editable: boolean;
}

/** An API error's reason for the player, or `fallback`. */
export const errorReason = (error: unknown, fallback: string) => (error instanceof Error && error.message ? error.message : fallback);

const CACHE_KEY = "pocket-play.controllerProfiles";
const SAVE_DELAY_MS = 1000;
const listeners = new Set<() => void>();
let profiles: ControllerProfile[] = readCache();

function readCache(): ControllerProfile[] {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) ?? "[]") as unknown;
    return Array.isArray(cached) ? (cached as ControllerProfile[]) : [];
  } catch {
    return [];
  }
}

function set(next: ControllerProfile[]) {
  profiles = next;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(next));
  } catch {
    // Kept for this page at least.
  }
  for (const listener of listeners) listener();
}

/** Runs `listener` whenever the list changes; returns the way to stop. */
export function onControllersChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Every profile, by name; the same array until something changes. */
export const controllerProfiles = (): ControllerProfile[] => profiles;

export const profileById = (id: string): ControllerProfile | undefined => profiles.find((p) => p.id === id);

const byName = (list: ControllerProfile[]) => [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

/**
 * Reads the list again. Public, so it goes out signed out too (with the
 * bearer when there is one, for `editable`); never throws, and the cached
 * list stands when the cloud can't be reached.
 */
export async function loadControllers(): Promise<void> {
  if (!navigator.onLine) return;
  const token = session.bearer();
  try {
    const response = await fetchWithLogging(`${getDeploymentConfig().apiUrl}/controllers`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return;
    const body = (await response.json()) as { profiles: ControllerProfile[] };
    set(body.profiles);
  } catch {
    // The cached list stands.
  }
}

const json = (body: unknown): RequestInit => ({
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** Adds a profile with these keys; throws the API's reason when it can't. */
export async function createProfile(name: string, keys: Overrides): Promise<ControllerProfile> {
  const response = await apiRequest("/controllers", { method: "POST", ...json({ name, keys }) });
  const made = (await response.json()) as ControllerProfile;
  set(byName([...profiles, made]));
  return made;
}

async function update(profile: ControllerProfile): Promise<void> {
  const response = await apiRequest(`/controllers/${encodeURIComponent(profile.id)}`, {
    method: "PUT",
    ...json({ name: profile.name, keys: profile.keys }),
  });
  const saved = (await response.json()) as ControllerProfile;
  // Keys changed again while this was on its way stay as they are here.
  set(profiles.map((p) => (p.id === saved.id ? { ...saved, keys: p.keys } : p)));
}

export async function renameProfile(id: string, name: string): Promise<void> {
  const profile = profileById(id);
  if (profile) await update({ ...profile, name });
  set(byName(profiles));
}

const pending = new Map<string, number>();
let onSaveError: (error: unknown) => void = () => {};

/** Where a key change that couldn't be saved is told (the key panel shows it). */
export function onProfileSaveError(listener: (error: unknown) => void) {
  onSaveError = listener;
}

/**
 * Takes the keys at once, here, and sends them a second later (a few key
 * changes in a row are one write). If that fails, the cloud's list comes
 * back and the reason goes to `onProfileSaveError`.
 */
export function setProfileKeys(id: string, keys: Overrides) {
  set(profiles.map((p) => (p.id === id ? { ...p, keys } : p)));
  window.clearTimeout(pending.get(id));
  pending.set(
    id,
    window.setTimeout(() => {
      pending.delete(id);
      const profile = profileById(id);
      if (!profile) return;
      update(profile).catch((error: unknown) => {
        onSaveError(error);
        void loadControllers();
      });
    }, SAVE_DELAY_MS),
  );
}

/** Deletes it for everyone; games that used it play with Default. */
export async function deleteProfile(id: string): Promise<void> {
  await apiRequest(`/controllers/${encodeURIComponent(id)}`, { method: "DELETE" });
  set(profiles.filter((p) => p.id !== id));
}
